// File: /opt/stacks/workout/app/src/components/workout/ActiveWorkoutBanner.js
//
// Global persistent banner that renders on all views except Coach whenever a
// guided workout is active. The underlying timer continues running in
// WorkoutContext regardless of which view is displayed.

import React, { useContext } from "react";
import { AppStateContext, TimerContext } from "../../contexts/WorkoutContext";
import { Timer, ChevronRight, Pause, Play } from "lucide-react";
import usePhoneLandscape from "../../hooks/usePhoneLandscape";

// Formats raw seconds into mm:ss (or h:mm:ss for sessions over an hour).
const formatElapsed = (seconds) => {
  const s = Math.max(0, Math.floor(seconds) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const ActiveWorkoutBanner = () => {
  // Slow-moving: routing guard, coach metadata, navigation
  const { appState, view, setView, coachState, playPause } = useContext(AppStateContext);
  // On its side a phone has no bottom nav (the tabs sit in the header).
  const phoneLandscape = usePhoneLandscape();
  // Fast-ticking: elapsed counters that update every second
  const { coachTick } = useContext(TimerContext);

  // Only render when a workout is active and the user has navigated away from Coach.
  if (appState !== "active" || view === "coach") return null;

  const isGuidedActive = ["running", "paused", "readyGate"].includes(
    coachState.status,
  );
  if (!isGuidedActive) return null;

  // The Ready gate is a paused-style hold for banner display purposes.
  const isPaused =
    coachState.status === "paused" || coachState.status === "readyGate";
  const isTimed = ["s", "m"].includes(coachState.currentNode?.unit);
  const isCountUp = !!coachState.countUpMode;
  const title = coachState.currentNode?.label ?? "Workout in progress";
  const subtitle = isPaused
    ? "Paused — tap to resume"
    : "In progress — tap to return";
  // Count-up & rep-based phases show total elapsed; timed phases show countdown.
  const displayTime =
    isCountUp || !isTimed
      ? (coachTick.elapsedSessionTime ?? 0)
      : (coachTick.timeLeftInPhase ?? 0);
  const onPauseResume = playPause;

  const handleTimerTap = (e) => {
    e.stopPropagation();
    onPauseResume();
  };

  return (
    <button
      onClick={() => setView("coach")}
      // Floats above the mobile bottom nav (h-14 = bottom-14).
      // On desktop and a phone on its side the bottom nav is hidden, so we
      // use a smaller offset.
      className={`fixed ${phoneLandscape ? "bottom-4" : "bottom-16"} md:bottom-6 inset-x-4 md:inset-x-0 md:mx-auto md:w-96 z-40
                 flex items-center justify-between gap-3
                 bg-primary text-primary-foreground rounded-xl px-4 py-3
                 shadow-lg shadow-primary/30 ring-1 ring-white/10
                 animate-in slide-in-from-bottom-2 duration-300`}
      aria-label="Return to active workout"
    >
      {/* Left: live-state indicator + workout label */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="relative shrink-0">
          <Timer className="h-5 w-5" />
          {/* Green pulse dot for running state; absent when paused */}
          {!isPaused && (
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-400 ring-1 ring-primary animate-pulse" />
          )}
        </div>
        <div className="text-left min-w-0">
          <p className="text-sm font-bold leading-tight truncate">{title}</p>
          <p className="text-xs opacity-70 leading-tight truncate">{subtitle}</p>
        </div>
      </div>

      {/* Right: tappable timer for pause/resume + chevron */}
      <div className="flex items-center gap-1.5 shrink-0">
        <span
          onClick={handleTimerTap}
          role="button"
          tabIndex={0}
          aria-label={isPaused ? "Resume timer" : "Pause timer"}
          className="group relative text-sm font-mono font-bold tabular-nums
                     px-2 py-1 -my-1 rounded-md
                     hover:bg-white/15 active:bg-white/25 transition-colors cursor-pointer"
        >
          {formatElapsed(displayTime)}
          {/* Hover overlay: show pause/play icon */}
          <span className="absolute inset-0 flex items-center justify-center rounded-md
                          bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
            {isPaused ? (
              <Play className="h-4 w-4 text-white fill-current" />
            ) : (
              <Pause className="h-4 w-4 text-white" />
            )}
          </span>
        </span>
        <ChevronRight className="h-4 w-4 opacity-60" />
      </div>
    </button>
  );
};

export default ActiveWorkoutBanner;
